import { useMemo, useState } from 'react';

export interface ModelAttempt<T> {
  /** The result for the current input, or the last valid one when the input is invalid. */
  result: T | null;
  /** The specific message for the current input, or null when it is valid. */
  error: string | null;
}

/**
 * Runs a pure, throwing calculation for the current input and keeps the last valid result while
 * the input is invalid (CT-INTERACT C4, fail closed). `calculate` must be stable across renders
 * (a module-level function) so the memo only recalculates when the input changes.
 */
export function useLastValidResult<I, T>(input: I, calculate: (input: I) => T): ModelAttempt<T> {
  const attempt = useMemo(() => {
    try {
      return { value: calculate(input), error: null };
    } catch (error) {
      return { value: null, error: error instanceof Error ? error.message : 'This input cannot be modelled.' };
    }
  }, [input, calculate]);
  const [last, setLast] = useState<T | null>(attempt.value);
  if (attempt.value !== null && attempt.value !== last) setLast(attempt.value);
  return { result: attempt.value ?? last, error: attempt.error };
}
